import {layer, source} from "azure-maps-control";
import SymbolLayer = layer.SymbolLayer;
import DataSource = source.DataSource;
import { AzureCriteriaAdapter } from "@esWebAPI/es/util/AzureCriteriaAdapter";
import {AzureOverlay} from "@esWebAPI/es/map/module/AzureOverlay";
import _ from "lodash";

import type {Map, SymbolLayerOptions, Expression} from "azure-maps-control";
import type TerritoryModel from "@esWebAPI/es/map/TerritoryModel";
import type {ITerritoryActions, ITerritoryFeature} from "@esWebAPI/es/map/TerritoryModel";
import type { TerritoryOutlinesLayer } from "./TerritoryOutlinesLayer";
import {IEventChannel} from "@esWebAPI/es/model/Models";

export const TERRITORY_LABEL_LAYER_ID = "territory-label-layer";

/**
 * Await next mapbox idle event, plus a defensive timeout so  caller can never hang if idle fails to fire
 * e.g. listener attached while already idle and nothing subsequently triggers a render.
 */
function waitForMapIdle(map: Map, timeoutMs = 2000): Promise<void> {
    return new Promise<void>(resolve => {
        let resolved = false;
        const isDone = () => {
            if (!resolved) {
                resolved = true
                resolve();
            }
        };
        map.events.addOnce("idle", isDone);
        setTimeout(isDone, timeoutMs);
    });
}

export class TerritoryLabelLayer {
    private readonly _territoryModel: TerritoryModel;
    private readonly _layer: SymbolLayer;
    private _datasource: DataSource = new DataSource("territory-label-source");
    private readonly map;
    private readonly eventChannel;
    private readonly territoryOutlinesLayer: TerritoryOutlinesLayer

    private readonly bindUpdateDatasourceFeatures = this.updateDatasourceFeatures.bind(this);
    private readonly bindOnTerritoryFilterChange = this.onTerritoryFilterChange.bind(this);
    private readonly bindOnTerritoryChange = this.onTerritoryChange.bind(this);
    private readonly bindOnTerritoryLevelChange = this.onTerritoryLevelChange.bind(this);
    private readonly bindOnUpdateLayer = this.onUpdateLayer.bind(this);

    private readonly bindOnMapMove = _.throttle(this.updateDatasourceFeatures.bind(this), 500);


    constructor(map: Map, territoryModel: TerritoryModel, territoryOutlinesLayer: TerritoryOutlinesLayer, eventChannel: IEventChannel) {
        this._territoryModel = territoryModel;
        this.eventChannel = eventChannel;
        this.map = map;
        this.territoryOutlinesLayer = territoryOutlinesLayer;

        const defaultLayerOpts = {
            iconOptions: {
                allowOverlap: false,
                ignorePlacement: false,
                opacity: 0,
                image: "none"
            },
            textOptions: {
                ignorePlacement: false,
                allowOverlap: false,
                textField: ['get', 'label'],
                font: ["Arimo-Bold"],
                size: 20,
                haloWidth: 1,
                haloBlur: 0,
                haloColor: "#000000",
                color: '#FFFFFF'
            },
            filter: this.getTerritoryFilter(territoryModel.getFilter()),
            // territory level filter is handled on the Outlines Worker. We pass the level over when getting the VisualCenterFeatures
            visible: territoryModel.getManageTerritoryMainLayerView().getStyleSettingsObjRef().labelSettings.enabled
        } as SymbolLayerOptions;

        this.map.sources.add(this._datasource);
        this._layer = new SymbolLayer(this._datasource.getId(), TERRITORY_LABEL_LAYER_ID, defaultLayerOpts);
        try {
            this.map.layers.add(this._layer, AzureOverlay.SYMBOL_LAYER_ID);
        } catch(e) {
            console.error("Err adding ter label layer layer", e);
        }

        eventChannel.addListener("onTerritoryOutlinesLayerUpdate", this.bindUpdateDatasourceFeatures);
        eventChannel.addOnce("onTerritoryGenerateOutlinesFinish", this.bindUpdateDatasourceFeatures);

        eventChannel.addListener("onTerritoryFilterChange", this.bindOnTerritoryFilterChange);
        eventChannel.addListener("onTerritoryChange", this.bindOnTerritoryChange);
        eventChannel.addListener("onTerritoryLevelChange", this.bindOnTerritoryLevelChange);

        eventChannel.addListener("onUpdateLayer", this.bindOnUpdateLayer, this);

        this.map.events.add("move", this.bindOnMapMove);
    }

    private updateDatasourceFeatures() {
        // Maybe we will need to create a beskope function to handle the map move
        
        const bounds = this.map.getCamera().bounds as [number, number, number, number];
        this.territoryOutlinesLayer.onReady.then(() => {
            this.territoryOutlinesLayer.getVisualCenterFeatures(bounds, this._territoryModel.getCurrentLevel(), (features) => {
                this._datasource.clear();
                const active = this._territoryModel.getActive(true, true).map(t => t.ES_ID);

                for(const centerFeature of features) {
                    const territory = this._territoryModel.get(centerFeature.id);
                    if (territory && active.includes(territory.ES_ID)) {
                        centerFeature.properties.label = territory.NAME;
                        centerFeature.properties.color = es.util.color.serverToHex(territory.ES_STYLE_COLOR);
                        this._datasource.add(centerFeature);
                    }
                }
                // update filter
                this.onTerritoryFilterChange("CHANGE_FILTER", active);
                this.eventChannel.fireEvent("onTerritoryLabelsUpdate");
            });
        });
    }

    private async onTerritoryFilterChange(_action: "CHANGE_FILTER", territoryIds: number[]) {
        const options = this.getOptions();
        options.filter = AzureCriteriaAdapter.merge(
            this.getLevelFilter(this._territoryModel.getCurrentLevel()),
            this.getTerritoryFilter(territoryIds));
        this.setOptions(options);
        // setOptions returns void but cascades through setFilter -> _updateWorkerLayers which is async
        // wait here for the next idle (plus safety timeout!) so callers can rely on the new filter being applied before continuing.
        await waitForMapIdle(this.map);
    }

    private onTerritoryChange(actions: ITerritoryActions, territories: ITerritoryFeature[], _changesTerritoryIds: number[]) {
        // These changes are being listening on the outlines update.
        if(actions.MOVE || actions.REDO || actions.UNDO || actions.RELOAD || actions.ADD_FEATS || actions.REMOVE_FEATS || actions.SELECT_FEATS || actions.ADD_TERRITORY || actions.CHANGE_ATTRIBUTE) {
            return;
        }

        if(actions.CHANGE_NAME) {
            for(const territory of territories) {
                const shape = this._datasource.getShapeById(territory.ES_ID);
                if (shape) { // if there's no assigned base features, it won't be in the datasource
                    const properties = this._datasource.getShapeById(territory.ES_ID).getProperties();
                    // The properties are passed by reference, so we do not need to manually set the properties via the api
                    properties.label = territory.NAME;
                }
            }
        }

        if(actions.CHANGE_COLOR) {
            for(const territory of territories) {
                const shape = this._datasource.getShapeById(territory.ES_ID);
                if (shape) { // if there's no assigned base features, it won't be in the datasource
                    // The properties are passed by reference, so we do not need to manually set the properties via the api
                    shape.getProperties().color = es.util.color.serverToHex(territory.ES_STYLE_COLOR);
                }
            }
        }
    }

    private async onTerritoryLevelChange(level: number) {
        const options = this._layer.getOptions();
        options.filter = this.getLevelFilter(level);
        this.setOptions(options);
        await waitForMapIdle(this.map);
    }

    private onUpdateLayer(layerViewModel: any) {
        if (this._territoryModel.getManageTerritoryMainLayerView().getId() === layerViewModel.getId()) {
            this._layer.setOptions({visible: layerViewModel.getStyleSettingsObjRef().labelSettings.enabled})
        }
    }

    private getLevelFilter(level: number|null): Expression | undefined {
        if(level == null) {
            return undefined;
        }
        return ['==', ['get', 'level'], level];
    }

    private getTerritoryFilter(territoryIds: number[]|null): Expression | undefined{
        if(territoryIds == undefined || territoryIds.length === 0) {
            return undefined;
        }
        return ['in', ['id'], ['literal', territoryIds]];
    }

    destroy(): void {
        this.eventChannel.removeListener("onTerritoryOutlinesLayerUpdate", this.bindUpdateDatasourceFeatures);
        this.eventChannel.removeListener("onTerritoryGenerateOutlinesFinish", this.bindUpdateDatasourceFeatures);
        this.eventChannel.removeListener("onTerritoryFilterChange", this.bindOnTerritoryFilterChange);
        this.eventChannel.removeListener("onTerritoryChange", this.bindOnTerritoryChange);
        this.eventChannel.removeListener("onTerritoryLevelChange", this.bindOnTerritoryLevelChange);
        this.map.events.remove("move", this.bindOnMapMove);
        this.map.layers.remove(this._layer);
        this.map.sources.remove(this._datasource);
    }

    getOptions(): SymbolLayerOptions {
        return this._layer.getOptions();
    }

    hide(): void {
        this._layer.setOptions({visible: false});
    }

    isVisible(): boolean {
        return this._layer.getOptions().visible == undefined || this._layer.getOptions().visible === true;
    }

    setOptions(options: SymbolLayerOptions): void {
        this._layer.setOptions(options);
    }

    show(): void {
        this._layer.setOptions({visible: true});
    }

    toggle(): void {
        this._layer.setOptions({visible: !this.isVisible()});
    }

    getLayerIds(): string[] {
        return [this._layer.getId()];
    }
}
